import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Col, Card, Row, Form, Button } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FormValidator } from '../../helpers/form-validator';
import { toMoment } from '../../helpers/moment-ex';
import { getWhatIfPlan, whatIfPlan, getWhatIfInfo, whatIfInfo } from '../../actions/crew';
import { Unit, roundOfDays } from '../../helpers/unit';
import { CardContainer, Validator, NumberInput, Heading5, TitleIcon, Heading6 } from '../widgets';
import GraphContainer from '../graph/GraphContainer';
import { DISPLAY_DATE_FORMAT2 } from '../../constants';
import { usePermission } from '../../helpers/common';

class WhatIf extends React.Component {
    constructor() {
        super();
        this.unit = Unit.getInstance();
        this.validator = new FormValidator(this.getRules());
        this.returnUrl = "";
        this.submitted = false;

        this.defaultCrewPlan = {
            acres: 0,
            utilization: 0,
            avgWorkingSpeed: 0,
            workingHoursPerDay: 0,
            nonWorkingDays: 0
        };

        this.defaultWhatIf = {
            rowCapacity: 0,
            ...this.defaultCrewPlan
        };

        this.state = {
            ...this.defaultWhatIf,
            crewPlan: { ...this.defaultCrewPlan },
            previousWhatIf: { ...this.defaultWhatIf },
            validation: this.validator.valid(),
            farmId: 0,
            planTypeId: 0,
            crewId: 0,
            validateGroup: null,
            remainingAcres: 0,
            crewName: "",
            endDate: null,
            selectedEqGroups: []
        };

        this.handleUpdate = this.handleUpdate.bind(this);
        this.bindWhatIfInfo = this.bindWhatIfInfo.bind(this);
        this.whatIfCalculate = this.whatIfCalculate.bind(this);
        this.disableCalculateBtn = this.disableCalculateBtn.bind(this);
    }

    getRules() {
        const speed = { min: this.unit.toRoundOfSpeed(0.5), max: this.unit.toRoundOfSpeed(50) };

        return [{
            field: 'rowCapacity',
            method: 'isEmpty',
            validWhen: false,
            message: 'Row Capacity is required.',
            group: 'whatifplan'
        },
        {
            field: 'rowCapacity',
            method: 'isInt',
            validWhen: true,
            args: [{ min: 0 }],
            validateEmpty: false,
            message: 'Row Capacity minimum is {min}',
            group: "whatifplan"
        },
        {
            field: 'utilization',
            method: 'isEmpty',
            validWhen: false,
            message: 'Utilization is required.',
            group: "whatifplan"
        },
        {
            field: 'utilization',
            method: 'isFloat',
            validWhen: true,
            args: [{ min: 1, max: 100 }],
            validateEmpty: false,
            message: 'Utilization between {min} and {max}%',
            group: "whatifplan"
        },
        {
            field: 'avgWorkingSpeed',
            method: 'isEmpty',
            validWhen: false,
            message: 'Avg. working speed is required.',
            group: "whatifplan"
        },
        {
            field: 'avgWorkingSpeed',
            method: 'isFloat',
            validWhen: true,
            args: [{ min: speed.min, max: speed.max }],
            validateEmpty: false,
            message: 'Avg. working speed between {min} and {max}',
            group: "whatifplan"
        },
        {
            field: 'workingHoursPerDay',
            method: 'isEmpty',
            validWhen: false,
            message: 'Working hours per day is required.',
            group: "whatifplan"
        },
        {
            field: 'workingHoursPerDay',
            method: 'isFloat',
            validWhen: true,
            args: [{ min: 1, max: 24 }],
            validateEmpty: false,
            message: 'Working hours per day between {min} and {max}',
            group: "whatifplan"
        },
        {
            field: 'nonWorkingDays',
            method: 'isInt',
            validWhen: true,
            args: [{ min: 0, max: 50 }],
            validateEmpty: false,
            message: 'Non-working days maximum is {max}',
            group: "whatifplan"
        }];
    }

    componentDidMount() {
        const { farmId, id } = this.props.match.params;
        this.returnUrl = `/home/farm/${farmId}/map/crew/${id}`;
        this.props.actions.getWhatIfInfo(farmId, id);
        this.setState({ crewId: id, farmId });
    }

    componentDidUpdate(prevProps, prevState) {
        if (Object.keys(this.props.whatIfInfo).length !== 0 && this.props.whatIfInfo !== prevProps.whatIfInfo)
            this.bindWhatIfInfo();

        if (Object.keys(this.state.crewPlan).length !== 0 && this.state.crewPlan !== prevState.crewPlan)
            this.whatIfCalculate();
    }

    bindWhatIfInfo() {
        const c = this.props.whatIfInfo;
        const crewPlan = {
            acres: this.unit.toArea(parseFloat(c.acres), 2),
            utilization: parseInt(c.utilization),
            avgWorkingSpeed: this.unit.toSpeed(parseFloat(c.avg_working_speed), 2),
            workingHoursPerDay: parseFloat(c.working_hours_per_day),
            nonWorkingDays: parseInt(c.non_working_days),
            rowCapacity: Math.round(c.width) || 0
        };

        const startDate = c.plan_start_date ? toMoment(c.plan_start_date) : null;
        const endDate = c.plan_end_date ? toMoment(c.plan_end_date) : null;

        this.setState({
            ...crewPlan,
            crewPlan: { ...crewPlan },
            crewName: c.crew_name,
            planTypeId: (c.plan_type_id || 0),
            remainingAcres: (c.remaining_acres || 0),
            startDate,
            endDate
        });
    }

    whatIfCalculate() {
        if (this.isValidateWhatIfPlan()) {
            const s = this.state;

            const whatIf = {
                rowCapacity: s.rowCapacity,
                utilization: s.utilization,
                avgWorkingSpeed: s.avgWorkingSpeed,
                workingHoursPerDay: s.workingHoursPerDay,
                nonWorkingDays: s.nonWorkingDays,
            };

            const params = {
                farm_uuid: s.farmId,
                plan_type_id: s.planTypeId,
                acres: s.remainingAcres,
                width: Number(s.rowCapacity),
                utilization: parseFloat(s.utilization),
                avg_working_speed: this.unit.fromSpeed(parseFloat(s.avgWorkingSpeed)),
                working_hours_per_day: parseFloat(s.workingHoursPerDay),
                non_working_days: Number(s.nonWorkingDays) ? parseInt(s.nonWorkingDays) : 0,
                equipment_group_list: []
            };

            this.setState({ previousWhatIf: whatIf });
            this.props.actions.getWhatIfPlan(params);
        } else {
            this.setState({ previousWhatIf: { ...this.defaultWhatIf } });
            this.props.actions.whatIfPlan();
        }
    }

    isValidateWhatIfPlan() {
        const validateGroup = "whatifplan";
        const validation = this.validator.validate(this.state, validateGroup);
        this.setState({ validation, validateGroup });
        this.submitted = true;
        return validation.isValid;
    }

    handleUpdate(event) {
        const { name, value } = event.target;
        this.setState({ [name]: value });
    }

    componentWillUnmount() {
        this.props.actions.whatIfInfo();
        this.props.actions.whatIfPlan();
    }

    disableCalculateBtn() {
        const curr = this.state;
        const prev = this.state.previousWhatIf;

        return (parseFloat(curr.rowCapacity) === parseFloat(prev.rowCapacity)
            && parseFloat(curr.utilization) === parseFloat(prev.utilization)
            && parseFloat(curr.avgWorkingSpeed) === parseFloat(prev.avgWorkingSpeed)
            && parseFloat(curr.workingHoursPerDay) === parseFloat(prev.workingHoursPerDay)
            && parseFloat(curr.nonWorkingDays) === parseFloat(prev.nonWorkingDays));
    }

    render() {
        const v = this.submitted ? this.validator.validate(this.state, this.state.validateGroup) : this.state.validation;
        const settings = this.props.settings;
        const s = this.state;
        const crewPlan = this.state.crewPlan
        const whatIfInfo = this.props.whatIfPlan
        const unit = this.unit;
        const allow = usePermission();
        const isAllowEdit = allow("whatif", "edit");

        return (
            <CardContainer lg={10} md={10} className="crew-form whatif">
                <Card className="card-form">
                    <Card.Header>
                        <Card.Title>
                            <Heading5 className="d-inline">
                                <TitleIcon className="fa fa-area-chart" />What If
                            </Heading5>
                            <Heading6 className="d-inline">
                                {this.props.whatIfInfo.crew_name ? " - " + this.props.whatIfInfo.crew_name : ''}
                            </Heading6>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col lg={7} md={7}>
                                <Row className="summary-crew mb-0">
                                    <Col lg={6} md={6} className="sum-crew-inr">
                                        <h6>Remaining Acres</h6>
                                        <hr />
                                        <div className="summary-val">{roundOfDays(s.remainingAcres || 0)}</div>
                                    </Col>
                                    <Col lg={6} md={6} className="sum-crew-inr">
                                        <h6>Projected Remaining Days</h6>
                                        <hr />
                                        <div className="summary-val">{roundOfDays(whatIfInfo.total_working_days)}</div>
                                    </Col>
                                </Row>
                                <Row className="summary-crew mb-0">
                                    <Col lg={6} md={6} className="sum-crew-inr">
                                        <h6>Projected {settings.areaShortName}/day</h6>
                                        <hr />
                                        <div className="summary-val">{unit.toRoundOfArea(whatIfInfo.acres_per_day)}</div>
                                    </Col>
                                    <Col lg={6} md={6} className="sum-crew-inr">
                                        <h6>Projected {settings.areaShortName}/hour</h6>
                                        <hr />
                                        <div className="summary-val">{unit.toRoundOfArea(whatIfInfo.acres_per_hour)}</div>
                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col lg={4}>
                                    </Col>
                                    <Col lg={4}>
                                        <Form.Label className="text-primary-light">Planned</Form.Label>
                                    </Col>
                                    <Col lg={4}>
                                        <Form.Label className="text-primary-light">What If</Form.Label>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4} md={4}>
                                        <Form.Label className="whatif">End Date</Form.Label>
                                    </Col>
                                    <Col lg={4} md={4}>
                                        <Form.Group>
                                            <Form.Label>
                                                {(s.endDate) ? s.endDate.format(DISPLAY_DATE_FORMAT2) : '-'}
                                            </Form.Label>
                                        </Form.Group>
                                    </Col>

                                    <Col lg={4} md={4}>
                                        <Form.Group>
                                            <Form.Label>
                                                {(whatIfInfo.end_date) ? toMoment(whatIfInfo.end_date).format(DISPLAY_DATE_FORMAT2) : '-'}
                                            </Form.Label>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4} md={4}>
                                        <Form.Label className="whatif">Harvester Row Capacity</Form.Label>
                                    </Col>
                                    <Col lg={3} md={3}>
                                        <Form.Group>
                                            <Form.Label>{(crewPlan.rowCapacity) ? crewPlan.rowCapacity : 0}</Form.Label>
                                        </Form.Group>
                                    </Col>
                                    <Col lg={5} md={5}>
                                        <Form.Group className={`${!isAllowEdit && "event-disabled"}`}>
                                            <Validator validation={v.rowCapacity}>
                                                <NumberInput name="rowCapacity" value={s.rowCapacity} onUpdate={this.handleUpdate} />
                                                <Validator.Text />
                                            </Validator>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4} md={4}>
                                        <Form.Label className="whatif" htmlFor="txtPlannedArea">Utilization</Form.Label>
                                    </Col>
                                    <Col lg={3} md={3}>
                                        <Form.Group>
                                            <Form.Label>{crewPlan.utilization + ' %'}</Form.Label>
                                        </Form.Group>
                                    </Col>

                                    <Col lg={5} md={5}>
                                        <Form.Group className={`${!isAllowEdit && "event-disabled"}`}>
                                            <Validator validation={v.utilization}>
                                                <NumberInput name="utilization" value={s.utilization} onUpdate={this.handleUpdate} />
                                                <Validator.Text />
                                            </Validator>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4} md={4}>
                                        <Form.Label className="whatif" htmlFor="txtAvgWorkingSpeed">Avg. Working Speed</Form.Label>
                                    </Col>
                                    <Col lg={3} md={3}>
                                        <Form.Group>
                                            <Form.Label>{crewPlan.avgWorkingSpeed + ' ' + settings.speedShortName}</Form.Label>
                                        </Form.Group>
                                    </Col>

                                    <Col lg={5} md={5}>
                                        <Form.Group className={`${!isAllowEdit && "event-disabled"}`}>
                                            <Validator validation={v.avgWorkingSpeed}>
                                                <NumberInput name="avgWorkingSpeed" value={s.avgWorkingSpeed} type="double" onUpdate={this.handleUpdate} />
                                                <Validator.Text />
                                            </Validator>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4} md={4}>
                                        <Form.Label className="whatif" htmlFor="txtWorkingHoursPerDay">Working Hours Per Day</Form.Label>
                                    </Col>
                                    <Col lg={3} md={3}>
                                        <Form.Group>
                                            <Form.Label>{crewPlan.workingHoursPerDay + ' Hours'}</Form.Label>
                                        </Form.Group>
                                    </Col>

                                    <Col lg={5} md={5}>
                                        <Form.Group className={`${!isAllowEdit && "event-disabled"}`}>
                                            <Validator validation={v.workingHoursPerDay}>
                                                <NumberInput name="workingHoursPerDay" value={s.workingHoursPerDay} onUpdate={this.handleUpdate} />
                                                <Validator.Text />
                                            </Validator>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4} md={4}>
                                        <Form.Label className="whatif">Non-Working Days</Form.Label>
                                    </Col>
                                    <Col lg={3} md={3}>
                                        <Form.Group>
                                            <Form.Label>{crewPlan.nonWorkingDays + ' Days'}</Form.Label>
                                        </Form.Group>
                                    </Col>

                                    <Col lg={5} md={5}>
                                        <Form.Group className={`${!isAllowEdit && "event-disabled"}`}>
                                            <Validator validation={v.nonWorkingDays}>
                                                <NumberInput name="nonWorkingDays" value={s.nonWorkingDays} onUpdate={this.handleUpdate} />
                                                <Validator.Text />
                                            </Validator>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>
                            <Col lg={5} md={5} className="whatif-graph-hldr">
                                <GraphContainer type="official" graphData={whatIfInfo.graph} height={300}
                                    settings={{ areaShortName: this.props.settings.areaShortName }} />
                            </Col>
                        </Row >
                    </Card.Body>
                    <Card.Footer>
                        <Row>
                            <Col lg={7} md={7}>
                                {isAllowEdit && <Button variant="primary" size="sm" className="float-right mx-1 btn-calculate"
                                    onClick={this.whatIfCalculate} disabled={this.disableCalculateBtn()}>
                                    <i className="fa fa-calculator"></i> Calculate
                                </Button>}
                                <NavLink to={this.returnUrl}>
                                    <Button variant="danger" size="sm" className="float-right mx-2">
                                        <i className="fa fa-times-circle"></i> Cancel
                                    </Button>
                                </NavLink>
                            </Col>
                        </Row>
                    </Card.Footer>
                </Card>
            </CardContainer>);
    }
}

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({ getWhatIfInfo, whatIfInfo, getWhatIfPlan, whatIfPlan }, dispatch)
});

const mapStateToProps = state => ({
    settings: state.user.settings,
    whatIfPlan: state.crew.whatIfPlan,
    whatIfInfo: state.crew.whatIfInfo
});

export default connect(mapStateToProps, mapDispatchToProps)(WhatIf);